import { HomePageTitlesEnum } from './../../modules/home-page/models/home-page-title.model';
import { HttpParams, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs';
import { ISearchProjectModel, ISearchProjectShippingHistoryModel, IShippingHistoryPayloadModel, IFilePayloadModel } from '@tt-chvac/order';
import { AppConstants } from '../constants/constants';
import { JobsService } from '@tt-chvac/job';
import { DisplayModeEnum } from '../enums/display-mode-enum';
import { RevenueStreamEnum } from '../enums/revenue-stream-enum';
import { IJobProjectResponse } from '../model/project-roles-model';
import { IODataBatchResponse, IRequest, IRequestPayload } from '../model/time-entry';

/**
 * Optional search parameters grouped to avoid exceeding the maximum parameter count.
 */
export interface ISearchOptions {
  currentDataSelected?: string;
  jobId?: number;
  isCommissionSplitExcluded?: boolean;
  isProductFamilySelected?: boolean;
  timeframeConstraint?: Date;
  revenueStreamType?: string;
}

@Injectable({
  providedIn: 'root',
})
export class GlobalSearchService {
  public BASE_URL: string;
  public fetchedData = new Subject<any>();
  public searchValue = new Subject<string>();
  public TabName = new BehaviorSubject(null);
  public tabName: string;
  public searchVal: string;
  public options: HttpParams;
  public workPackage = 'Work Packages';
  public shippingHistoryDownloadUrl: string;
  public source: string;
  public isCommissionSplitExcluded = true;
  public revenueStreamType: RevenueStreamEnum;
  private readonly ERP_BATCH_CHUNK_SIZE = 50;

  constructor(private http: HttpClient, private appConstants: AppConstants, private jobsService: JobsService) { }

  /**
   * Triggers a search for the given tab, resolving and storing the selected revenue stream type.
   * @param searchText - The search text entered by the user.
   * @param tabName - The currently selected tab (e.g. 'Projects', 'Work Packages', 'Jobs').
   * @param drAddressId - The selected DR address id.
   * @param options - Optional search parameters (toggle, jobId, filters, category, etc.).
   */
  searchList(
    searchText: string,
    tabName: string,
    drAddressId: number,
    options: ISearchOptions = {}): void {
    const {
      currentDataSelected,
      jobId,
      isCommissionSplitExcluded = true,
      isProductFamilySelected = false,
      timeframeConstraint,
      revenueStreamType,
    } = options;
    this.options = null;
    this.revenueStreamType = RevenueStreamEnum[revenueStreamType as keyof typeof RevenueStreamEnum];
    if (tabName && drAddressId) {
      // other tabs will be integrated later
      if (tabName === this.workPackage) {
        this.tabName = tabName;
        if (searchText !== '') {
          const options = new HttpParams()
            .set('searchText', searchText);
          this.options = options;
        } else if (jobId > 0) {
          const options = new HttpParams()
            .set('jobId', jobId.toString());
          this.options = options;
        } else {
          this.options = null;
        }
        this.BASE_URL = this.appConstants.API_BASE_URL_WORKPACKAGE + '/' + drAddressId + '/WorkPackages';
      } else if (tabName === 'Projects' && searchText !== '') {
        this.setToggleBaseURL(tabName, searchText, currentDataSelected);
        this.isCommissionSplitExcluded = isCommissionSplitExcluded;
      } else if (tabName === HomePageTitlesEnum.Jobs) {
        if (isProductFamilySelected) {
          this.jobsService.useDate.next(true);
          this.jobsService.lastDate.next(timeframeConstraint);
          this.jobsService.triggerProductFamilySearch.next(searchText);
        } else {
          this.jobsService.useDate.next(false);
          this.jobsService.triggerSearch.next(searchText);
        }
      } else if (tabName === HomePageTitlesEnum.DelegatedJobs) {
        this.jobsService.useDate.next(false);
        this.jobsService.triggerSearch.next(searchText);
      }
      // sets the search value
      this.TabName.next(this.tabName);
      this.searchValue.next(searchText);
      if (tabName === this.workPackage) {
        this.fetchList().subscribe((data) => {
          // sets the searched data
          this.fetchedData.next(data);
        }, (err) => {
          this.fetchedData.next(err);
        });
      }
    }
  }

  /**
   * Sets the BASE_URL according to toggle value selected
   * * @returns void
   */
  setToggleBaseURL(tabName: string, searchText: string, currentDataSelected: string): void {
    this.tabName = tabName;
    this.searchVal = searchText;
    if (currentDataSelected === 'Transmitted' || currentDataSelected === 'Projects') {
      this.BASE_URL = `${this.appConstants.API_BASE_URL_ORDERGATEWAY}/CreditJobs`;
    } else if (currentDataSelected === 'History') {
      this.BASE_URL = `${this.appConstants.API_BASE_URL_ORDER_HISTORY}/CreditJobs`;
    } else if (currentDataSelected === 'Untransmitted') {
      this.BASE_URL = `${this.appConstants.API_BASE_URL_ORDERGATEWAY}/CreditJobs`;
    } else if (currentDataSelected === 'Shipping History') {
      this.BASE_URL = `${this.appConstants.API_BASE_URL_SHIPPING_HISTORY}/Orders/SearchableItems`;
    }
  }

  fetchList(currentToggle?: string, drAddressList?: number[]) {
    if (currentToggle === DisplayModeEnum.Transmitted || currentToggle === DisplayModeEnum.History
      || currentToggle === DisplayModeEnum.Untransmitted) {
      return this.http
        .post(`${this.BASE_URL}`, drAddressList, { params: this.options })
        .pipe(map((response: any[]) => this.transformData(response)))
        .pipe(catchError((error: HttpErrorResponse) => throwError(error)))
    } else {
      return this.http
        .get(`${this.BASE_URL}`, { params: this.options })
        .pipe(map((response: any[]) => this.transformData(response)))
        .pipe(catchError((error: HttpErrorResponse) => throwError(error)))
    }
  }

  getOrderList(skip, take, drAddressId, currentToggle?: string): Observable<ISearchProjectModel> {
    if (currentToggle === DisplayModeEnum.History) {
      this.options = new HttpParams()
        .set('searchValue', this.searchVal).set('skip', skip).set('take', take);
      this.options = this.appendRevenueStreamType(this.options);
      return this.fetchList(currentToggle, drAddressId);
    } else if (currentToggle === DisplayModeEnum.Untransmitted) {
      this.source = 'Ordering';
      this.options = new HttpParams()
        .set('searchValue', this.searchVal).set('skip', skip).set('take', take).set('source', this.source);
      this.options = this.appendRevenueStreamType(this.options);
      return this.fetchList(currentToggle, drAddressId);
    } else if (currentToggle === DisplayModeEnum.Transmitted) {
      this.source = 'Order';
      this.options = new HttpParams()
        .set('searchValue', this.searchVal).set('skip', skip).set('take', take)
        .set('source', this.source).set('isCommissionSplitExcluded', this.isCommissionSplitExcluded.toString());
      this.options = this.appendRevenueStreamType(this.options);
      return this.fetchList(currentToggle, drAddressId);
    }
  }

  /**
   * Appends `revenueStreamType` to the project search params when a category is set.
   * @param params - The existing HttpParams for the project search request.
   * @returns The params with `revenueStreamType` appended when set, otherwise unchanged.
   */
  private appendRevenueStreamType(params: HttpParams): HttpParams {
    if (this.revenueStreamType) {
      return params.set('revenueStreamType', this.revenueStreamType);
    }
    return params;
  }

  /**
   * Fetches contracting roles via ERP OData $batch for the given project numbers (max 50 per chunk).
   * @param projectNumbers - The project number values to fetch contracting roles for.
   * @param filterField - The OData field to filter on (`erpProjectNumber` or `number`).
   * @returns Observable of the OData $batch response containing the job-projects and their roles.
   */
  getProjectRolesInBatch(
    projectNumbers: string[],
    filterField: string = this.appConstants.JOB_PROJECT_ODATA_FILTER_FIELD.erpProjectNumber
  ): Observable<IODataBatchResponse<IJobProjectResponse>> {
    const requests: IRequestPayload[] = this.chunkArray(projectNumbers, this.ERP_BATCH_CHUNK_SIZE)
      .map((projectNumberChunk: string[], chunkIndex: number) =>
        this.buildJobProjectBatchRequestItem(projectNumberChunk, filterField, chunkIndex));
    const payload = { requests } as IRequest;
    const headers = new HttpHeaders()
      .set('X-ApiKey', this.appConstants.ERP_API_KEY)
      .set('Content-Type', 'application/json');
    return this.http.post<IODataBatchResponse<IJobProjectResponse>>(this.appConstants.ERP_BATCH_ODATA_URL, payload, { headers });
  }

  /**
   * Splits an array into chunks of at most `chunkSize` items each.
   * @param items - The array to split into chunks.
   * @param chunkSize - The maximum number of items per chunk.
   * @returns An array of chunks, each containing at most `chunkSize` items.
   */
  private chunkArray<T>(items: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];
    for (let chunkStart = 0; chunkStart < items.length; chunkStart += chunkSize) {
      chunks.push(items.slice(chunkStart, chunkStart + chunkSize));
    }
    return chunks;
  }

  /**
   * Builds a single OData $batch request item that filters job-projects by the given project numbers.
   * @param projectNumberChunk - The project numbers to include in this request's OData filter.
   * @param filterField - The OData field to filter on (`erpProjectNumber` or `number`).
   * @param chunkIndex - The zero-based index of this chunk, used to build a unique request id.
   * @returns The OData $batch request item for this chunk.
   */
  private buildJobProjectBatchRequestItem(
    projectNumberChunk: string[], filterField: string, chunkIndex: number): IRequestPayload {
    const filter = `${filterField} in (${projectNumberChunk.map((projectNumber: string) => `'${projectNumber}'`).join(',')})`;
    return {
      id: `${chunkIndex + 1}`,
      method: 'GET',
      url: `job-projects?$filter=${filter}`,
    };
  }

  /**
   * Gets the shipping history pdf
   * @param {string} - payload contains document key
   * @returns file - shipping history pdf
   */
  downloadPDF(documentKey: string): Observable<{ file: Blob }> {
    this.options = new HttpParams().set('documentKey', documentKey);
    const shippingHistoryDownloadUrl = `${this.appConstants.API_BASE_URL_SHIPPING_HISTORY}/Orders/Document/Download`;
    return this.http.get(shippingHistoryDownloadUrl, { params: this.options, observe: 'response', responseType: 'blob' })
      .pipe(map((response) => {
        return {
          file: new Blob([response.body], { type: response.headers.get('Content-Type') }),
        };
      }), catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * Gets the shipping history details
   * @param {IShippingHistoryPayloadModel} - payload that contains paging and sorting options
   * @returns ISearchProjectShippingHistoryModel - shipping history response
   */
  getShippingHistory(payload: IShippingHistoryPayloadModel): Observable<ISearchProjectShippingHistoryModel> {
    return this.http.post<ISearchProjectShippingHistoryModel>(`${this.BASE_URL}`,
      payload, { observe: 'response' }).pipe(map(
        (res) => {
          if (res.status === 200) {
            return res.body;
          } else if (res.status === 204) {
            return { pagingItems: [], pageNumber: 0, pageSize: 0, totalItemCount: 0, pageCount: 0 };
          }
        }), catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * Download File
   * @param {payload} -  payload for pdf request
   */
  downloadFile(payload: IFilePayloadModel): Observable<string> {
    this.shippingHistoryDownloadUrl = `${this.appConstants.API_BASE_URL_SHIPPING_HISTORY}/Orders/PcbHeaders/GeneratePdf`;
    return this.http.post(this.shippingHistoryDownloadUrl, payload, { responseType: 'text' })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * Download vo report
   * @param {documentKey} - document Key
   * @param {orderType} - the order type (VO|SIL)
   */
  downloadShippingHistoryReports(documentKey: string, orderName: string, serialNumber?: string, orderType?: string)
    : Observable<{ file: Blob, filename: string }> {
    let options = new HttpParams()
      .set('documentKey', documentKey)
      .set('orderNumber', orderName)
      .set('orderType', orderType || 'VO');
    if (serialNumber != null) {
      options = options.set('serialNumber', serialNumber);
    }
    this.shippingHistoryDownloadUrl = `${this.appConstants.API_BASE_URL_DOCUMENT}/ShippingHistoryDocuments/Download`;
    return this.http.get(this.shippingHistoryDownloadUrl, { observe: 'response', responseType: 'blob', params: options })
      .pipe(map((res) => {
        if (res.status === 200) {
          const contentDisposition = res.headers.get('content-disposition');
          return {
            file: new Blob([res.body], { type: res.headers.get('Content-Type') }),
            filename: this.getFilenameFromContentDisposition(contentDisposition).trim()
          };
        } else {
          return null;
        }
      }), catchError((error: HttpErrorResponse) => throwError(error)));
  }

  transformData(data: any) {
    const searchedList = data;
    if (data && this.tabName === 'Work Packages') {
      searchedList.map((workPackage: any) => {
        // transform the bidDate, stateDate and endDate to date format.
        workPackage.bidDate = workPackage.bidDate ? new Date(workPackage.bidDate).setHours(0, 0, 0) : '';
        workPackage.startDate = workPackage.startDate ? new Date(workPackage.startDate).setHours(0, 0, 0) : '';
        workPackage.endDate = workPackage.endDate ? new Date(workPackage.endDate).setHours(0, 0, 0) : '';
        // trasform null to ' ' so that we can filter null values in multiselect
        workPackage.salesStage = workPackage.salesStage === null ? ' ' : workPackage.salesStage;
        return workPackage;
      });
    }
    return searchedList;
  }

  /**
   * Parses a filename from the content disposition header
   * @param {contentDisposition} - the content disposition header value
   */
  getFilenameFromContentDisposition(contentDisposition: string): string {
    const regex = /filename=(?<filename>[^,;]+);/g;
    const match = regex.exec(contentDisposition);
    // remove any whitespace (trim) or quotes
    return match.groups.filename.trim().replace(/\"/g, '');
  }
}
